import { useEffect, useRef, useState } from "react"
import {
  GooglePlacePrediction,
  PlaceTypeAheadSearchResult,
} from "@/types/place/place-search-input"
import debounce from "lodash/debounce"
import { v4 as uuidv4 } from "uuid"

import { httpIsCancelled } from "@/lib/exception-utils"
import {
  SearchPlacesArgs,
  useResolvePlaceMutation,
  useSearchPlacesMutation,
} from "@/lib/storage/api/place-api"

type UsePlaceTypeaheadOptions = {
  getSearchArgs: (query: string) => SearchPlacesArgs
  minChars?: number
  onResults?: (results: PlaceTypeAheadSearchResult, query: string) => void
}

export function usePlaceTypeahead({
  getSearchArgs,
  minChars = 3,
  onResults,
}: UsePlaceTypeaheadOptions) {
  const [typeaheadResults, setTypeaheadResults] =
    useState<PlaceTypeAheadSearchResult | null>(null)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [resolvingGooglePlaceId, setResolvingGooglePlaceId] = useState<
    string | null
  >(null)

  const inFlightSearchRef = useRef<{ abort: () => void } | null>(null)
  const lastSearchValueRef = useRef("")
  const sessionTokenRef = useRef<string | undefined>(undefined)
  const getSearchArgsRef = useRef(getSearchArgs)
  const onResultsRef = useRef(onResults)
  const minCharsRef = useRef(minChars)

  const [searchPlacesMutation] = useSearchPlacesMutation()
  const [resolvePlaceMutation] = useResolvePlaceMutation()
  const searchPlacesMutationRef = useRef(searchPlacesMutation)

  getSearchArgsRef.current = getSearchArgs
  onResultsRef.current = onResults
  minCharsRef.current = minChars
  searchPlacesMutationRef.current = searchPlacesMutation

  const debouncedTypeaheadRef = useRef(
    debounce(async (query: string) => {
      if (query.length < minCharsRef.current) {
        setTypeaheadResults(null)
        return
      }

      try {
        inFlightSearchRef.current?.abort()
        sessionTokenRef.current ||= uuidv4()

        const request = searchPlacesMutationRef.current(
          getSearchArgsRef.current(query)
        )
        inFlightSearchRef.current = request

        const results = await request.unwrap()
        setTypeaheadResults(results)
        onResultsRef.current?.(results, query)
      } catch (e) {
        if (!httpIsCancelled(e)) {
          console.error("Typeahead error:", e)
        }
      }
    }, 300)
  )

  useEffect(() => {
    const debouncedTypeahead = debouncedTypeaheadRef.current
    return () => {
      debouncedTypeahead.cancel()
      inFlightSearchRef.current?.abort()
    }
  }, [])

  const runTypeahead = (query: string) => {
    if (query !== lastSearchValueRef.current && query.length >= minChars) {
      lastSearchValueRef.current = query
      void debouncedTypeaheadRef.current(query)
    }
  }

  const resetTypeahead = () => {
    debouncedTypeaheadRef.current.cancel()
    inFlightSearchRef.current?.abort()
    setTypeaheadResults(null)
    setHighlightedIndex(-1)
    lastSearchValueRef.current = ""
  }

  const resolveGooglePlace = async (googlePlace: GooglePlacePrediction) => {
    try {
      setResolvingGooglePlaceId(googlePlace.place_id)
      const token = sessionTokenRef.current || uuidv4()
      sessionTokenRef.current = token
      return await resolvePlaceMutation({
        placeType: googlePlace.entity_type,
        gpId: googlePlace.place_id,
        sessionToken: token,
      }).unwrap()
    } finally {
      setResolvingGooglePlaceId(null)
    }
  }

  return {
    typeaheadResults,
    setTypeaheadResults,
    highlightedIndex,
    setHighlightedIndex,
    resolvingGooglePlaceId,
    lastSearchValueRef,
    runTypeahead,
    resetTypeahead,
    resolveGooglePlace,
  }
}
